// @owners { team: patients-team }
import { BORDERS, COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import { AltoIcon, Column, Touchable, XxsPadding } from '@alto/design-system';
import React, { useState } from 'react';
import { ImageBackground } from 'react-native';
import { SvgXml } from 'react-native-svg';
import styled from 'styled-components/native';
import { MedImageModal } from './med-list/MedImageModal';
import { PlayButtonXml } from './playButtonXml';

const LARGE = {
  width: SIZES.ILLUSTRATION.LG.value,
  height: SIZES.ILLUSTRATION.MD.value,
};
const MEDIUM = {
  width: SIZES.ILLUSTRATION.MD.value,
  height: SIZES.ILLUSTRATION.SM.value,
};

type BaseImageContainerProps = {
  size: {
    width: number;
    height: number;
  };
};
const BaseImageContainer = styled(Column).attrs({
  center: true,
})<{ size: { width: number; height: number } }>`
  border-radius: ${BORDERS.RADIUS.XS.px};
  overflow: hidden;
  ${({ size: { width, height } }) => `
    width: ${width}px;
    height: ${height}px;
  `}
`;

type MedicationImageContainerProps = BaseImageContainerProps & {
  outline?: boolean;
};
const MedicationImageContainer = styled(BaseImageContainer)<MedicationImageContainerProps>`
  background-color: ${COLORS.PALETTE.GREYSCALE.DEFAULT};
  overflow: hidden;
  border-radius: ${BORDERS.RADIUS.MD.px};
  ${({ outline }) =>
    outline
      ? `
  padding: 2px;
  border: ${SPACING.STATIC.XXS.px} solid ${COLORS.BORDER_COLORS.DARK};
  `
      : ''}
`;

const PlaceholderContainer = styled(BaseImageContainer)`
  background-color: ${COLORS.PALETTE.GREYSCALE.DEFAULT};
`;

const MedicationImage = styled(ImageBackground)<{ size: { width: number; height: number }; children: React.ReactNode }>`
  border-radius: ${BORDERS.RADIUS.XS.px};
  ${({ size: { width, height } }) => `
    width: ${width}px;
    height: ${height}px;
  `}
`;

const VideoIcon = () => (
  <SvgXml
    xml={PlayButtonXml}
    height={SIZES.ICON.LG.value}
    width={SIZES.ICON.LG.value}
  />
);

type Props = {
  readonly lg?: boolean;
  readonly outline?: boolean;
  readonly imageUrl?: string | null | undefined;
  readonly clickable?: boolean;
  readonly hasVideos?: boolean;
};

export const MedImage = ({ lg, outline, imageUrl, clickable = true, hasVideos }: Props) => {
  const [showMedicationImageModal, setShowMedicationImageModal] = useState(false);

  const getImageSize = () => {
    const size = lg ? LARGE : MEDIUM;
    const { width, height } = size;
    const smallerSize = {
      width: width - BORDERS.RADIUS.SM.value * 2,
      height: height - BORDERS.RADIUS.SM.value * 2,
    };
    return outline ? smallerSize : size;
  };

  const handleOnPress = () => {
    if (clickable) {
      toggleMedicationImageModal();
    }
  };

  const toggleMedicationImageModal = () => {
    setShowMedicationImageModal(!showMedicationImageModal);
  };

  const containerSize = lg ? LARGE : MEDIUM;
  const imageSize = getImageSize();

  if (imageUrl) {
    return (
      <>
        <MedicationImageContainer
          size={containerSize}
          outline={outline}
        >
          <Touchable
            borderRadius={BORDERS.RADIUS.XS.value}
            disabled={!clickable}
            onPress={handleOnPress}
            // @ts-expect-error TS(2322): Type '{ children: Element; borderRadius: number; disabled: boolean; onPress: () => void; underlayCol... (Delete me to see the full error)
            underlayColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTER}
          >
            <MedicationImage
              size={imageSize}
              source={{
                uri: imageUrl,
              }}
            >
              {hasVideos ? (
                <Column>
                  <XxsPadding>
                    <VideoIcon />
                  </XxsPadding>
                </Column>
              ) : null}
              {clickable ? (
                <Column
                  bottom
                  right
                  // eslint-disable-next-line react-native/no-inline-styles
                  style={{ height: '100%', width: '100%' }}
                >
                  <XxsPadding>
                    <AltoIcon
                      name="search"
                      type="white"
                    />
                  </XxsPadding>
                </Column>
              ) : null}
            </MedicationImage>
          </Touchable>
        </MedicationImageContainer>
        <MedImageModal
          show={showMedicationImageModal}
          source={imageUrl}
          onClose={toggleMedicationImageModal}
        />
      </>
    );
  }

  return (
    <MedicationImageContainer
      size={containerSize}
      outline={outline}
    >
      <PlaceholderContainer size={imageSize}>
        {hasVideos ? (
          <VideoIcon />
        ) : (
          <AltoIcon
            name="pillbottle"
            type="grey"
          />
        )}
      </PlaceholderContainer>
    </MedicationImageContainer>
  );
};
